import React from 'react';
import { TFunction } from 'i18next';
import {
	Box,
	InputAdornment,
	MenuItem,
	Select,
	SelectChangeEvent,
	SxProps,
	TextField,
	Theme,
} from '@mui/material';
import {
	BufferTimeUnit,
	BufferTimeUnits,
	clampBufferTimeUnitAmount,
	EMPTY_FIXED_BUFFER_TIME,
	FixedBufferTime,
	getBufferTimeUnitLabel,
	getBufferTimeUnitMaxAmount,
} from 'common/modules/atoms/bufferTimes';

export interface FixedBufferTimePickerProps {
	value?: FixedBufferTime | undefined;
	onChange?: (value: FixedBufferTime) => void;
	disabled?: boolean;
	sx?: SxProps<Theme>;
	t: TFunction;
}

const FixedBufferTimePicker = (props: FixedBufferTimePickerProps) => {
	const { value = EMPTY_FIXED_BUFFER_TIME.value, onChange, disabled, sx, t } = props;

	const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const amount = parseInt(event.target.value, 10);
		onChange?.({
			...value,
			amount: clampBufferTimeUnitAmount(value.unit, amount),
		});
	};

	const handleUnitChange = (e: SelectChangeEvent<BufferTimeUnit>) => {
		const unit = e.target.value as BufferTimeUnit;
		onChange?.({
			...value,
			unit,
			amount: clampBufferTimeUnitAmount(unit, value.amount),
		});
	};

	return (
		<Box
			mt={2}
			display="flex"
			flexDirection="column"
			width={{ xs: '100%', sm: 'auto' }}
			alignItems={{ xs: 'stretch', sm: 'flex-start' }}
			sx={sx}
		>
			<TextField
				label=""
				variant="outlined"
				margin="none"
				type="number"
				placeholder="0"
				disabled={disabled}
				name="days"
				value={value.amount}
				onChange={handleAmountChange}
				InputProps={{
					inputProps: {
						min: 0,
						max: getBufferTimeUnitMaxAmount(value.unit),
					},
					endAdornment: (
						<InputAdornment position="end">
							<Select
								variant="standard"
								disableUnderline
								value={value.unit ?? BufferTimeUnits.days}
								onChange={handleUnitChange}
							>
								<MenuItem value={BufferTimeUnits.minutes}>
									{getBufferTimeUnitLabel({
										amount: value.amount,
										unit: BufferTimeUnits.minutes,
										t,
									})}
								</MenuItem>
								<MenuItem value={BufferTimeUnits.hours}>
									{getBufferTimeUnitLabel({
										amount: value.amount,
										unit: BufferTimeUnits.hours,
										t,
									})}
								</MenuItem>
								<MenuItem value={BufferTimeUnits.days}>
									{getBufferTimeUnitLabel({
										amount: value.amount,
										unit: BufferTimeUnits.days,
										t,
									})}
								</MenuItem>
								<MenuItem value={BufferTimeUnits.weekDays}>
									{getBufferTimeUnitLabel({
										amount: value.amount,
										unit: BufferTimeUnits.weekDays,
										t,
									})}
								</MenuItem>
								<MenuItem value={BufferTimeUnits.openingDays}>
									{getBufferTimeUnitLabel({
										amount: value.amount,
										unit: BufferTimeUnits.openingDays,
										t,
									})}
								</MenuItem>
							</Select>
						</InputAdornment>
					),
				}}
			/>
		</Box>
	);
};

export default FixedBufferTimePicker;
